import { Handshake, Search, Banknote, ShieldCheck, Users, Briefcase, Home, Wrench } from "lucide-react";
import { Link } from "react-router-dom";
import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { PartnerSignupForm } from "@/components/PartnerSignupForm";

/**
 * Partner & referral program page. Signup form posts source: 'partner_signup'.
 * Deliberately publishes NO hard commission numbers - those live in the partner
 * agreement (pending internal approval); the page sells the mechanism.
 * No invented earnings claims, no fabricated partner logos (proof integrity).
 */

const faqs = [
  {
    q: "How does the Frayze partner program work?",
    a: "You introduce a business that needs its leads handled: missed calls answered, follow-up automated, a website that converts. We scope it, sell it, and deliver it. When they sign and pay, you earn a commission on the build and the first year of their monthly service. You never do the selling.",
  },
  {
    q: "Who makes a good Frayze partner?",
    a: "People that small businesses already trust: accountants and bookkeepers, business consultants, realtors, marketing freelancers who don't do CRM or automation work, and people connected in the automotive world. If owners ask you 'who should I talk to about my website or my leads', you're the person this program is for.",
  },
  {
    q: "When do I get paid?",
    a: "Commissions are paid on signed, paying clients, never on clicks or leads. The exact rates, timing, and terms are in your partner agreement, which we walk through together before your first referral.",
  },
  {
    q: "Do I have to sell anything?",
    a: "No. A warm introduction is the whole job. We handle the audit, the proposal, the pricing conversation, and the delivery. You stay the trusted advisor who made a good call.",
  },
  {
    q: "Is there a quota or a cost to join?",
    a: "No cost, no quota, no exclusivity. Refer one business a year or one a month. You're paid the same way on each.",
  },
  {
    q: "Do I have to tell people I'm a partner?",
    a: "Yes. Canadian competition law requires disclosing a material connection when you recommend a business you're compensated by, and we'd want it that way regardless: the program only works if your recommendation stays trustworthy. One plain sentence is enough.",
  },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const Partners = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Partner & Referral Program | Frayze"
        description="Refer businesses that need their leads handled: missed calls, follow-up, websites that convert. Frayze scopes, sells, and delivers; you earn a commission on the build and the first year of service. No quota, no cost to join."
        keywords="frayze partner program, agency referral program, refer a business, marketing referral partner, thunder bay business referral"
        canonical="https://frayze.ca/partners/"
        structuredData={faqJsonLd}
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-primary/5 via-background to-background">
          <div className="container mx-auto px-4 py-16 sm:py-24">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Partners</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary">
                Partner &amp; Referral Program
              </p>
              <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl">
                Refer businesses you already know. Get paid when they sign.
              </h1>
              <p className="mt-5 text-lg text-muted-foreground">
                Small-business owners already ask you who to trust. When one of them is
                losing leads to missed calls, slow follow-up, or a website that doesn't
                convert, introduce them to Frayze. We scope it, sell it, and deliver it,
                and you earn a commission on the build and the first year of their service.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#apply"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold text-white transition hover:bg-primary-hover"
                >
                  Apply to the program
                </a>
                <Link
                  to="/case-studies/global-aluminum-lead-generation/"
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 font-semibold transition hover:border-primary/50"
                >
                  See a client result
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="container mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold">How it works</h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: Handshake,
                title: "1. You introduce",
                body: "A warm intro to a business that needs its leads handled. That's the whole job: no pitching, no pricing talk, no follow-up chasing.",
              },
              {
                icon: Search,
                title: "2. We scope, sell, and deliver",
                body: "Frayze audits their lead flow, builds the proposal, closes the deal, and does the work: websites, CRM, automation, the full catch-the-lead system.",
              },
              {
                icon: Banknote,
                title: "3. You get paid on signed clients",
                body: "A commission on the build and the first year of their monthly service. Paid on signed, paying clients, never on clicks or leads. Exact terms in your partner agreement.",
              },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
                <s.icon className="h-8 w-8 text-primary" />
                <h3 className="mt-4 text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Who it's for */}
        <section className="border-y border-border bg-card/40">
          <div className="container mx-auto px-4 py-16">
            <h2 className="text-3xl font-bold">Built for the people owners already trust</h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">
              This is not an affiliate-link scheme. It's a professional referral program for
              people with real relationships in the small-business world.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: Briefcase, label: "Accountants & bookkeepers", sub: "You see who's losing money to missed leads before anyone else does." },
                { icon: Users, label: "Consultants & coaches", sub: "Hand your clients a working system instead of another recommendation." },
                { icon: Home, label: "Realtors & their networks", sub: "Referral-dense worlds where a good vendor intro is currency." },
                { icon: Wrench, label: "Freelancers & industry connectors", sub: "You do the design or the deals. We handle the CRM and automation." },
              ].map((w) => (
                <div key={w.label} className="rounded-xl border border-border bg-background p-5">
                  <w.icon className="h-6 w-6 text-primary" />
                  <div className="mt-3 font-semibold">{w.label}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{w.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust / disclosure */}
        <section className="container mx-auto px-4 py-16">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-bold">Your name stays safe on it</h2>
              <ul className="mt-6 space-y-4">
                {[
                  "Your referral gets a real audit and an honest proposal: if we're not the right fit, we say so and you look better for it.",
                  "Commissions are paid on signed clients only, so there's never pressure to push volume.",
                  "Disclosure is built in: partners tell their contacts about the relationship. One plain sentence: it's the law, and it's also why the program works.",
                  "No quota, no cost, no exclusivity. Refer when it genuinely fits.",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <ShieldCheck className="mt-0.5 h-5 w-5 flex-none text-primary" />
                    <span className="text-muted-foreground">{t}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-muted-foreground">
                Want the proof first? Read the{" "}
                <Link to="/case-studies/global-aluminum-lead-generation/" className="font-medium text-primary underline-offset-2 hover:underline">
                  Global Aluminum case study
                </Link>:{" "}
                audited numbers, no invented metrics.
              </p>
            </div>
            <div id="apply">
              <PartnerSignupForm />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-card/40">
          <div className="container mx-auto px-4 py-16">
            <h2 className="text-3xl font-bold">Partner program FAQ</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2">
              {faqs.map((f) => (
                <div key={f.q} className="rounded-xl border border-border bg-background p-6">
                  <h3 className="font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Partners;
